import axiosInstance from "./AxiosInstance";
import store from "@/store"; // Import the Vuex store
import { auth } from "@/plugins/firebaseconfig";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile,
  signInWithCredential,
  OAuthProvider,
} from "firebase/auth";

/**
 * UserAPI provides methods for interacting with user authentication and user-related API endpoints.
 */
const UserAPI = {
  joinWaitlist: async (email) => {
    const body = { email: email };
    const response = await axiosInstance.post(`/invitation/waitList`, body);

    return response.data;
  },
  /**
   * Fetch user by id
   *
   * @param {string} userId - The email address to check for an existing user.
   * @returns {Promise<boolean>} - A promise that resolves to true if the user exists, false otherwise.
   * @throws {Error} - Throws an error if the API call fails.
   */
  getUser: async (userId) => {
    try {
      const response = await axiosInstance.get(`/user/${userId}`);

      return response.data;
    } catch (error) {
      // Handles specific error for non-existing user
      if (
        error.response &&
        error.response.status === 404 &&
        error.response.data.reason.includes(`User with email '${email}'`)
      ) {
        return false;
      }
      throw error; // Rethrows error if it's a different issue
    }
  },

  getUserCards: async (userId) => {
    try {
      const response = await axiosInstance.get(`/stripe/cards/${userId}`);

      return response.data;
    } catch (error) {
      // Handles specific error for non-existing user
      if (
        error.response &&
        error.response.status === 404 &&
        error.response.data.reason.includes(`User with email '${email}'`)
      ) {
        return false;
      }
      throw error; // Rethrows error if it's a different issue
    }
  },

  addUserAddress: async (userId, address) => {
    const response = await axiosInstance.post(
      `/user/addAddress/${userId}`,
      address
    );
    return response.data;
  },

  detachUserCard: async (userId, cardId) => {
    try {
      const body = {
        cardId: cardId,
      };
      const response = await axiosInstance.post(
        `/stripe/cards/detach/${userId}`,
        body
      );

      return response.data;
    } catch (error) {
      throw error; // Rethrows error if it's a different issue
    }
  },

  /**
   * Verify phone number
   *
   * @param {string} phoneNumber - The phone number to check for an existing user.
   * @returns {Promise<boolean>} - A promise that resolves to true if the user exists, false otherwise.
   * @throws {Error} - Throws an error if the API call fails.
   */
  verifyPhoneNumber: async (phoneNumber, code) => {
    try {
      const payload = {
        number: phoneNumber,
        code: code,
      };
      const response = await axiosInstance.post(
        `/user/checkVerification`,
        payload
      );

      return response.data;
    } catch (error) {
      // Handles specific error for non-existing user
      if (
        error.response &&
        error.response.status === 404 &&
        error.response.data.reason.includes(`User with email '${email}'`)
      ) {
        return false;
      }
      throw error; // Rethrows error if it's a different issue
    }
  },
  /**
   * Send verification code phone number
   *
   * @param {string} phoneNumber - The phone number to check for an existing user.
   * @returns {Promise<boolean>} - A promise that resolves to true if the user exists, false otherwise.
   * @throws {Error} - Throws an error if the API call fails.
   */
  sendCodePhoneNumber: async (phoneNumber) => {
    try {
      const payload = {
        number: phoneNumber,
      };
      const response = await axiosInstance.post(
        `/user/sendVerification`,
        payload
      );

      return response.data;
    } catch (error) {
      // Handles specific error for non-existing user
      if (
        error.response &&
        error.response.status === 404 &&
        error.response.data.reason.includes(`User with email '${email}'`)
      ) {
        return false;
      }
      throw error; // Rethrows error if it's a different issue
    }
  },

  /**
   * Checks if a user exists by their email address.
   *
   * @param {string} email - The email address to check for an existing user.
   * @returns {Promise<boolean>} - A promise that resolves to true if the user exists, false otherwise.
   * @throws {Error} - Throws an error if the API call fails.
   */
  checkUserExists: async (params) => {
    try {
      const response = await axiosInstance.get("/user/userExists/check", {
        params,
      });
      return response.data;
    } catch (error) {
      // Handles specific error for non-existing user
      if (
        error.response &&
        error.response.status === 404 &&
        error.response.data.reason.includes(`User with email '${email}'`)
      ) {
        return false;
      }
      throw error; // Rethrows error if it's a different issue
    }
  },

  /**
   * Updates user.
   *
   *
   * @returns {Promise<boolean>} - A promise that resolves to true if the user exists, false otherwise.
   * @throws {Error} - Throws an error if the API call fails.
   */
  updateUser: async (userId, body) => {
    try {
      const response = await axiosInstance.patch(`/user/${userId}`, body);
      return response.data;
    } catch (error) {
      // Handles specific error for non-existing user
      if (
        error.response &&
        error.response.status === 404 &&
        error.response.data.reason.includes(`User with email '${email}'`)
      ) {
        return false;
      }
      throw error; // Rethrows error if it's a different issue
    }
  },

  /**
   * Registers a new user with the backend.
   *
   * @param {string} uid - The Firebase UID.
   * @param {string} email - The user's email address.
   * @param {string} accountType - The account type (email, google, apple).
   * @returns {Promise<Object>} - A promise that resolves to the backend response data.
   * @throws {Error} - Throws an error if the registration process fails.
   */
  registerNewUser: async (
    firebaseUser,
    accountType,
    phone,
    marketing,
    essential
  ) => {
    try {
      const user = firebaseUser;

      const displayName = user.displayName || ""; // Default to an empty string if null or undefined

      // Split the displayName into an array of names, if available
      const nameParts = displayName.split(" ");

      // Set the first and last names, with defaults for "förnamn" and "efternamn"
      const firstName = nameParts[0] || "förnamn"; // If empty, use "förnamn"
      const lastName =
        nameParts.length > 1 ? nameParts.slice(1).join(" ") : "efternamn"; // If no last name, use "efternamn"

      const payload = {
        _id: firebaseUser.uid,
        phone: phone,
        marketing: marketing,
        essential: essential,
        phoneVerified: true,
        email: firebaseUser.email,
        accountType: accountType,
        firstName: firstName,
        lastName: lastName,
        stripeId: "string",
        shippingAddresses: [],
        cards: [],
      };

      const response = await axiosInstance.post("/user", payload);
      return response.data;
    } catch (error) {
      console.error("Error during user registration:", error);
      throw error; // Throws the error for further handling
    }
  },

  /**
   * Sign in a user with email and password.
   *
   * @param {string} email - The user's email address.
   * @param {string} password - The user's password.
   * @returns {Promise<Object>} - A promise that resolves to the user data and tokens.
   * @throws {Error} - Throws an error if the sign-in process fails.
   */
  signInWithEmail: async (email, password) => {
    try {
      // Firebase sign-in with email and password
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      /*const user = userCredential.user;

      // Get the ID token and refresh token
      const firebaseToken = await user.getIdToken();
      const refreshToken = user.refreshToken;

      // Send the Firebase token to the backend for login
      const response = await axiosInstance.post("/user/login", {
        firebaseToken,
        type: "email",
      });

      // Store the received JWT token and refresh token
      store.commit("setToken", response.data.token);
      store.commit("setRefreshToken", refreshToken);
*/
      return { firebaseUser: userCredential };
    } catch (error) {
      console.error("Error during sign in:", error);
      throw error; // Throws the error for further handling
    }
  },

  /**
   * Register a new user with email and password.
   *
   * @param {string} email - The user's email address.
   * @param {string} password - The user's password.
   * @returns {Promise<Object>} - A promise that resolves to the user data and tokens.
   * @throws {Error} - Throws an error if the sign-up process fails.
   */
  signUpWithEmail: async (email, password, firstName, lastName) => {
    try {
      // Firebase sign-up with email and password
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      const firebaseUser = userCredential.user;

      const displayName = `${firstName} ${lastName}`;

      await updateProfile(firebaseUser, {
        displayName: displayName, // Set the full name as the displayName
      });
      await firebaseUser.reload();
      return { firebaseUser: userCredential };
    } catch (error) {
      console.error("Error during sign up:", error);
      throw error; // Throws the error for further handling
    }
  },

  loginUser: async (firebaseToken) => {
    const response = await axiosInstance.post("/user/login", {
      firebaseToken,
      type: "jwt",
    });
    return response;
  },
  /**
   * Register a new user with email and password.
   *
   * @param {string} email - The user's email address.
   * @param {string} password - The user's password.
   * @returns {Promise<Object>} - A promise that resolves to the user data and tokens.
   * @throws {Error} - Throws an error if the sign-up process fails.
   */
  signInWithCredential: async (firebaseUser) => {
    try {
      // Firebase sign-up with email and password

      const user = firebaseUser.user;

      const refreshToken = user.refreshToken;
      // Login existing user in the backend
      const firebaseToken = await user.getIdToken();
      const response = await axiosInstance.post("/user/login", {
        firebaseToken,
        type: "jwt",
      });
      const backendUser = response.data;
      store.commit("setToken", response.data.token);

      // Store the refresh token
      store.commit("setRefreshToken", refreshToken);

      return { user: backendUser, token: store.state.token, refreshToken };
    } catch (error) {
      console.error("Error during sign up:", error);
      throw error; // Throws the error for further handling
    }
  },

  /**
   * Sign in a user with Google.
   *
   * @returns {Promise<Object>} - A promise that resolves to the user data and tokens.
   * @throws {Error} - Throws an error if the Google sign-in process fails.
   */
  signInWithGoogle: async () => {
    try {
      const provider = new GoogleAuthProvider();
      // Firebase sign-in with Google
      const userCredential = await signInWithPopup(auth, provider);

      // Access the user info
      const user = userCredential.user;

      // Get the user's display name
      const displayName = user.displayName;
      /* const user = userCredential.user;

      const uid = user.uid;
      const accessToken = user.accessToken;
      const refreshToken = user.refreshToken;
      const email = user.email;

      // Check if the user already exists in the backend
      const userExists = await UserAPI.checkUserExists(email);
      let backendUser;

      if (!userExists) {
        // Register new user in the backend if not exists
        backendUser = await UserAPI.registerNewUser(uid, email, "google");
      } else {
        // Login existing user in the backend
        const firebaseToken = await user.getIdToken();
        const response = await axiosInstance.post("/user/login", {
          firebaseToken,
          type: "google",
        });
        backendUser = response.data;
        store.commit("setToken", response.data.token);
      }

      // Store the refresh token
      store.commit("setRefreshToken", refreshToken);
*/
      return { firebaseUser: userCredential };
    } catch (error) {
      console.error("Error during Google sign in:", error);
      throw error; // Throws the error for further handling
    }
  },

  /**
   * Sign in a user with Apple.
   *
   * @returns {Promise<Object>} - A promise that resolves to the user data and tokens.
   * @throws {Error} - Throws an error if the Apple sign-in process fails.
   */
  signInWithApple: async () => {
    try {
      const provider = new OAuthProvider("apple.com");
      // Firebase sign-in with Apple
      const userCredential = await signInWithPopup(auth, provider);

      // Access the user info
      const user = userCredential.user;

      // Get the user's display name
      const displayName = user.displayName;
      /* const user = userCredential.user;

      const uid = user.uid;
      const accessToken = user.accessToken;
      const refreshToken = user.refreshToken;
      const email = user.email;

      // Check if the user already exists in the backend
      const userExists = await UserAPI.checkUserExists(email);
      let backendUser;

      if (!userExists) {
        // Register new user in the backend if not exists
        backendUser = await UserAPI.registerNewUser(uid, email, "apple");
      } else {
        // Login existing user in the backend
        const firebaseToken = await user.getIdToken();
        const response = await axiosInstance.post("/user/login", {
          firebaseToken,
          type: "apple",
        });
        backendUser = response.data.user;
        store.commit("setToken", response.data.token);
      }

      // Store the refresh token
      store.commit("setRefreshToken", refreshToken);
*/
      return { firebaseUser: userCredential };
    } catch (error) {
      console.error("Error during Apple sign in:", error);
      throw error; // Throws the error for further handling
    }
  },

  sendPhoneEmail: async (body) => {
    try {
      const response = await axiosInstance.post("/user/send/numberUsed", body);

      return response.data;
    } catch (error) {
      // Handles specific error for non-existing user
      if (
        error.response &&
        error.response.status === 404 &&
        error.response.data.reason.includes(`User with email '${email}'`)
      ) {
        return false;
      }
      throw error; // Rethrows error if it's a different issue
    }
  },
  removeUserAddress: async (userId, addressId) => {
    const response = await axiosInstance.delete(
      `/user/deleteAddress/${userId}/${addressId}`
    );
    return response.data;
  },
  changeUserAddress: async (userId, addressId, address) => {
    const response = await axiosInstance.patch(
      `/user/updateAddress/${userId}/${addressId}`,
      address
    );
    return response.data;
  },

  getInovices: async () => {
    const user = store.state.user;
    const response = await axiosInstance.get(`/user/getInvoices/${user._id}`);
    return response.data;
  },
  updateMemberShip: async (amount) => {
    const user = store.state.user;
    const body = {
      userId: user._id,
      amount: amount,
    };
    const response = await axiosInstance.post(
      `/stripe/updateStripeSubscriptionPayment`,
      body
    );
    return response.data;
  },
  pauseSubscription: async (nrMonths) => {
    const user = store.state.user;
    const body = {
      nrMonths: nrMonths,
    };
    const response = await axiosInstance.post(
      `/user/pauseSubscription/` + user._id,
      body
    );
    return response.data;
  },
  unpauseSubscription: async () => {
    const user = store.state.user;
    const response = await axiosInstance.post(
      `/user/unpauseSubscription/` + user._id
    );
    return response.data;
  },
  cancelSubscription: async () => {
    const user = store.state.user;
    const response = await axiosInstance.post(
      `/user/cancelSubscription/` + user._id
    );
    return response.data;
  },
  stopCancelSubscription: async () => {
    const user = store.state.user;
    const response = await axiosInstance.post(
      `/user/stopCancelSubscription/` + user._id
    );
    return response.data;
  },

  validateVoucherCode: async (code) => {
    const user = store.state.user;
    const body = {
      code: code,
    };
    const response = await axiosInstance.post(
      `/user/redeemVoucher/` + user._id,
      body
    );
    return response.data;
  },

  getMostCommon: async ( page, pageSize) => {
    const user = store.state.user;
    const response = await axiosInstance.get(
      `/user/mostCommon/${user._id}?limit=${pageSize}&page=${page}`
    );
    return response.data;
  },
};

export default UserAPI;
